import japanPrefectures from "@/assets/data/japan-prefectures.json";

const JAPAN_POSTAL_ADDRESS_SOURCE = "/data/japan-postal-addresses.json";
let japanPostalAddressList = [];
let japanPostalAddressLoadPromise = null;

export const JAPAN_COUNTRY_OPTIONS = [
  {
    label: "日本",
    value: "日本",
  },
];

export const JAPAN_PREFECTURE_LIST = japanPrefectures;

export const JAPAN_PREFECTURE_OPTIONS = japanPrefectures.map((item) => ({
  label: item.name,
  value: item.name,
  code: item.code,
}));

export const JAPAN_POSTAL_ADDRESS_LIST = japanPostalAddressList;

function normalizeJapanPostalAddressItem(item = {}) {
  const zipCode = normalizeJapanPostalCode(item.zipCode || item.postalCode);
  return {
    ...item,
    zipCode,
    postalCode: formatJapanPostalCode(zipCode),
  };
}

export function normalizeJapanPostalCode(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 7);
}

export function formatJapanPostalCode(value, options = {}) {
  const { appendTrailingSeparator = true } = options;
  const digits = normalizeJapanPostalCode(value);
  if (digits.length < 3) return digits;
  if (digits.length === 3) return appendTrailingSeparator ? `${digits}-` : digits;
  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
}

export function formatJapanPostalCodeForInput(value, previousValue = "") {
  const digits = normalizeJapanPostalCode(value);
  const previousDigits = normalizeJapanPostalCode(previousValue);
  const previousText = String(previousValue || "");
  const currentText = String(value || "");
  const isDeletingTrailingSeparator =
    previousText.endsWith("-") &&
    !currentText.endsWith("-") &&
    previousDigits === digits &&
    digits.length === 3;

  return formatJapanPostalCode(value, {
    appendTrailingSeparator: !isDeletingTrailingSeparator,
  });
}

export function normalizeJapanPhone(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 11);
}

export async function loadJapanPostalAddressList() {
  if (japanPostalAddressList.length) return japanPostalAddressList;
  if (japanPostalAddressLoadPromise) return japanPostalAddressLoadPromise;
  if (typeof fetch !== "function") return [];

  japanPostalAddressLoadPromise = fetch(JAPAN_POSTAL_ADDRESS_SOURCE)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load Japan postal addresses: ${response.status}`);
      }
      return response.json();
    })
    .then((items) => {
      japanPostalAddressList = Array.isArray(items)
        ? items.map(normalizeJapanPostalAddressItem)
        : [];
      JAPAN_POSTAL_ADDRESS_LIST.length = 0;
      for (let index = 0; index < japanPostalAddressList.length; index += 1000) {
        JAPAN_POSTAL_ADDRESS_LIST.push(
          ...japanPostalAddressList.slice(index, index + 1000)
        );
      }
      return japanPostalAddressList;
    })
    .catch((error) => {
      console.warn("[japanAddress] failed to load postal address list", error);
      japanPostalAddressLoadPromise = null;
      return [];
    });

  return japanPostalAddressLoadPromise;
}

export function getJapanPostalAddressSuggestions(value, limit = 8) {
  const digits = normalizeJapanPostalCode(value);
  if (digits.length < 3) return [];

  const exactMatches = [];
  const prefixMatches = [];

  JAPAN_POSTAL_ADDRESS_LIST.forEach((item) => {
    if (item.zipCode === digits) {
      exactMatches.push(item);
      return;
    }

    if (item.zipCode.startsWith(digits)) {
      prefixMatches.push(item);
    }
  });

  return [...exactMatches, ...prefixMatches].slice(0, limit);
}

export async function getJapanPostalAddressSuggestionsAsync(value, limit = 8) {
  const digits = normalizeJapanPostalCode(value);
  if (digits.length < 3) return [];
  await loadJapanPostalAddressList();
  return getJapanPostalAddressSuggestions(digits, limit);
}

export function formatJapanPostalAddressSuggestion(item = {}) {
  return [
    item.postalCode ? `〒${item.postalCode}` : "",
    item.province,
    item.city,
    item.district,
  ]
    .filter(Boolean)
    .join(" ");
}

export function buildJapanAddressFromPostalAddress(item = {}) {
  const zipCode = normalizeJapanPostalCode(item.zipCode || item.postalCode);
  return {
    countryCode: "JP",
    country: "日本",
    zipCode,
    postalCode: formatJapanPostalCode(zipCode),
    province: item.province || "",
    city: item.city || "",
    district: item.district || "",
    street: item.street || "",
    detailAddress: item.detailAddress || "",
  };
}

export function buildJapanFullName(form = {}) {
  if (form.userRealname) {
    return String(form.userRealname).trim();
  }
  return [form.lastName, form.firstName].filter(Boolean).join(" ").trim();
}

export function buildJapanAddressText(form = {}) {
  return [
    form.province || form.prefecture,
    form.city,
    form.district || form.addressLine1,
    form.street,
    form.detailAddress || form.addressLine2,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function buildJapanCustomerAddress(form = {}) {
  const postalCodeDigits = normalizeJapanPostalCode(form.zipCode || form.postalCode);
  const postalCode = formatJapanPostalCode(postalCodeDigits);
  const country = form.country || "日本";
  const countryCode = form.countryCode || "JP";
  const province = form.province || form.prefecture || "";
  const district = form.district || form.addressLine1 || "";
  const street = form.street || "";
  const detailAddress = String(
    form.detailAddress || form.addressLine2 || ""
  ).trim();
  const userMobile = normalizeJapanPhone(form.userMobile || form.phone);

  return {
    countryCode,
    country,
    zipCode: postalCode,
    postalCode,
    province,
    city: form.city || "",
    district,
    street,
    detailAddress,
    userRealname: buildJapanFullName(form),
    userMobile,
    mobileNo: userMobile,
  };
}
